/*
 * 维护单组件公共内核（与 wechat_web 中的同名文件保持一致）
 * - util      通用工具（滚动容器由宿主决定：PC 为 div.netops-body，手机为 window）
 * - store     组件共享状态，读写都会同步到 Formily 的 form.values
 * - h         轻量 DOM 构造函数
 * - api       后端请求层，请求体与旧流程 schema 中的 fetch 脚本保持一致
 * - registry  组件校验注册，平台【提交】时由 Formily 字段校验统一调用
 * - start     启动：读取平台初始值 -> GetHistoryVariables 回填 -> 挂载组件
 *
 * host（宿主适配层）需提供：
 *   read() / write(key, value) / subscribe(fn) / readonly()
 *   scrollEl() / isMobileUA() / locate(ctx) / scan() / uploadHeaders()
 */
export default function installCore(MW, host) {
  var w = window;

  /* ================= util ================= */
  var MOBILE_UA_RE =
    /phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone/i;
  function pad(n) {
    return (n < 10 ? "0" : "") + n;
  }
  var util = {
    /** 与原脚本 getNowFormatDate 一致：YYYY-MM-DD HH:mm:ss */
    now: function () {
      var d = new Date();
      return (
        d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) + " " +
        pad(d.getHours()) + ":" + pad(d.getMinutes()) + ":" + pad(d.getSeconds())
      );
    },
    clone: function (v) {
      return v == null ? v : JSON.parse(JSON.stringify(v));
    },
    isEmpty: function (v) {
      return v == null || v === "" || (Array.isArray(v) && v.length === 0);
    },
    /** 打卡移动端判断 */
    isMobileUA: function () {
      if (host.isMobileUA) return !!host.isMobileUA();
      return MOBILE_UA_RE.test(navigator.userAgent || "");
    },
    /** 视口是否为手机布局 */
    isMobileView: function () {
      return w.matchMedia("(max-width: 760px)").matches;
    },
    /** 域名类型：woa / tencent / qq */
    hostKind: function () {
      var h = location.host || "";
      if (/\.tencent\.com$/.test(h)) return "tencent";
      if (/\.qq\.com$/.test(h)) return "qq";
      return "woa";
    },
    /** 是否测试环境（决定工单跳转链接的域名） */
    isTestEnv: function () {
      var h = location.host || "";
      return /test|localhost|127\.0\.0\.1|preview/.test(h) || !/dcops/.test(h);
    },
    /** 滚动容器：宿主未指定时为 window */
    scroller: function () {
      var el = host.scrollEl && host.scrollEl();
      return el || null;
    },
    scrollY: function () {
      var el = util.scroller();
      if (el) return el.scrollTop;
      return w.scrollY || document.documentElement.scrollTop || 0;
    },
    scrollTo: function (top, smooth) {
      var el = util.scroller();
      var opt = { top: Math.max(0, top), behavior: smooth ? "smooth" : "auto" };
      if (el) el.scrollTo ? el.scrollTo(opt) : (el.scrollTop = opt.top);
      else w.scrollTo(opt);
    },
    /** 节点相对滚动容器内容顶部的位置 */
    pageTop: function (node) {
      var el = util.scroller();
      var base = el ? el.getBoundingClientRect().top : 0;
      return node.getBoundingClientRect().top - base + util.scrollY();
    },
    fileUrl: function (f) {
      if (!f) return "";
      return f.url || (f.response && f.response.FileList && f.response.FileList[0] && f.response.FileList[0].url) || "";
    },
    isImage: function (f) {
      var u = util.fileUrl(f) + " " + (f && f.name ? f.name : "");
      return /^data:image|\.(png|jpe?g|gif|webp|bmp|svg|heic)(\?|\s|$)/i.test(u);
    },
    uid: function () {
      return "u" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    },
    delay: function (ms) {
      return new Promise(function (r) {
        setTimeout(r, ms);
      });
    },
    errMsg: function (e) {
      if (!e) return "";
      if (typeof e === "string") return e;
      return e.message || e.errMsg || "";
    },
    /** 是否为网络类错误（断网 / 超时 / 网关不可达），区别于后端返回的业务错误 */
    isNetErr: function (e) {
      if (!e) return false;
      if (e.offline) return true;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
      return /Failed to fetch|NetworkError|Network Error|Load failed|network|timeout|网络|超时/i.test(util.errMsg(e));
    },
    /** 构造网络错误 */
    netErr: function (msg) {
      var e = new Error(msg || "网络不可用");
      e.offline = true;
      return e;
    },
    /** 超时包装：超时按网络错误处理 */
    timeout: function (p, ms, msg) {
      return new Promise(function (resolve, reject) {
        var t = setTimeout(function () { reject(util.netErr(msg || "网络超时")); }, ms);
        p.then(function (v) { clearTimeout(t); resolve(v); }, function (e) { clearTimeout(t); reject(e); });
      });
    },
  };
  MW.util = util;

  /* ================= net：网络状态（任一请求成功即在线，网络类失败即离线） ================= */
  MW.net = {
    offline: typeof navigator !== "undefined" && navigator.onLine === false,
    _ls: [],
    on: function (fn) {
      var ls = this._ls;
      ls.push(fn);
      return function () { var i = ls.indexOf(fn); if (i > -1) ls.splice(i, 1); };
    },
    set: function (off) {
      off = !!off;
      if (this.offline === off) return;
      this.offline = off;
      this._ls.slice().forEach(function (fn) { try { fn(off); } catch (e) { console.error(e); } });
    },
  };

  /* ================= store ================= */
  function push(keys, values) {
    if (!host.write) return;
    keys.forEach(function (k) {
      if (k === "*" || k === "$context") return;
      try {
        host.write(k, util.clone(values[k]));
      } catch (e) {
        console.error("[mw] 写入表单失败", k, e);
      }
    });
  }
  var store = {
    values: {},
    _ls: [],
    _pending: null,
    init: function (v) {
      this.values = v || {};
      this.emit(["*"]);
    },
    get: function (k) {
      return this.values[k];
    },
    /** opt.silent=true 时只改值不触发重渲染（用于输入框键入过程），但仍会同步到表单 */
    set: function (k, v, opt) {
      this.values[k] = v;
      push([k], this.values);
      if (!(opt && opt.silent)) this.emit([k]);
    },
    patch: function (obj, opt) {
      var self = this;
      var keys = Object.keys(obj || {});
      keys.forEach(function (k) {
        self.values[k] = obj[k];
      });
      push(keys, this.values);
      if (!(opt && opt.silent)) this.emit(keys);
    },
    /** 原地修改了对象/数组后手动通知（同时同步到表单） */
    touch: function () {
      var keys = [].slice.call(arguments);
      push(keys, this.values);
      this.emit(keys.length ? keys : ["*"]);
    },
    /** 把组件数据重新写回平台表单（平台用旧值改写了表单时） */
    repush: function (keys) {
      push(keys, this.values);
    },
    /** 外部（平台）修改了表单值：只拉取有差异的字段，不回写 */
    pull: function (next) {
      var self = this;
      var changed = [];
      Object.keys(next || {}).forEach(function (k) {
        if (JSON.stringify(next[k]) !== JSON.stringify(self.values[k])) {
          self.values[k] = next[k];
          changed.push(k);
        }
      });
      if (changed.length) this.emit(changed);
    },
    on: function (keys, fn) {
      var l = { keys: keys === "*" ? null : [].concat(keys), fn: fn };
      this._ls.push(l);
      var ls = this._ls;
      return function () {
        var i = ls.indexOf(l);
        if (i > -1) ls.splice(i, 1);
      };
    },
    emit: function (keys) {
      var self = this;
      if (!self._pending) {
        self._pending = {};
        Promise.resolve().then(function () {
          var changed = Object.keys(self._pending);
          self._pending = null;
          var all = changed.indexOf("*") > -1;
          self._ls.slice().forEach(function (l) {
            if (all || !l.keys || l.keys.some(function (k) { return changed.indexOf(k) > -1; })) {
              try {
                l.fn(changed);
              } catch (e) {
                console.error(e);
              }
            }
          });
        });
      }
      keys.forEach(function (k) {
        self._pending[k] = 1;
      });
    },
  };
  MW.store = store;

  /* ================= h：DOM 构造 ================= */
  function append(el, children) {
    children.forEach(function (c) {
      if (c == null || c === false || c === true) return;
      if (Array.isArray(c)) return append(el, c);
      el.appendChild(c instanceof Node ? c : document.createTextNode(String(c)));
    });
  }
  function h(tag, props) {
    var el = document.createElement(tag);
    var children = [].slice.call(arguments, 2);
    if (props) {
      Object.keys(props).forEach(function (k) {
        var v = props[k];
        if (v == null || v === false) return;
        if (k === "class") el.className = Array.isArray(v) ? v.filter(Boolean).join(" ") : v;
        else if (k === "style" && typeof v === "object") Object.assign(el.style, v);
        else if (k === "html") el.innerHTML = v;
        else if (k === "value") el.value = v;
        else if (k === "dataset") Object.assign(el.dataset, v);
        else if (k.indexOf("on") === 0 && typeof v === "function") el.addEventListener(k.slice(2).toLowerCase(), v);
        else if (v === true) el.setAttribute(k, "");
        else el.setAttribute(k, v);
      });
    }
    append(el, children);
    return el;
  }
  MW.h = h;
  /** 用新内容替换容器内容，并尽量保持滚动位置与焦点不跳动 */
  MW.mount = function (container, node) {
    var y = util.scrollY();
    var snap = MW.anchor.capture();
    var ae = document.activeElement;
    var fname = ae && container.contains(ae) && ae.getAttribute && ae.getAttribute("name");
    var sel = fname && typeof ae.selectionStart === "number" ? [ae.selectionStart, ae.selectionEnd] : null;
    var keep = {};
    [].forEach.call(container.querySelectorAll("[data-keep-scroll]"), function (el) { keep[el.getAttribute("data-keep-scroll")] = [el.scrollTop, el.scrollLeft]; });
    container.innerHTML = "";
    append(container, [node]);
    Object.keys(keep).forEach(function (k) {
      var el = container.querySelector('[data-keep-scroll="' + k + '"]');
      if (el) { el.scrollTop = keep[k][0]; el.scrollLeft = keep[k][1]; }
    });
    if (fname) {
      var n = container.querySelector('[name="' + fname + '"]');
      if (n && !n.disabled) {
        try { n.focus({ preventScroll: true }); } catch (e) { n.focus(); }
        if (sel) try { n.setSelectionRange(sel[0], sel[1]); } catch (e) {}
      }
    }
    if (!MW.anchor.restore(snap) && Math.abs(util.scrollY() - y) > 2) util.scrollTo(y);
  };

  /**
   * 操作锚点：记录最后一次点击 / 聚焦的控件（带 data-key 的字段或开关，没有时取所在检查项 / 步骤卡片）。
   * 重绘时若该控件仍在屏幕内，保持它在屏幕上的位置不变（例如结束步骤后检查项折叠，页面不再整体上移）；
   * 带 data-key 的控件同时作为"回到上次填写的位置"的定位目标。
   */
  function layoutTop(el) {
    var t = 0;
    while (el) { t += el.offsetTop; el = el.offsetParent; }
    return t;
  }
  function viewRect() {
    var sc = util.scroller();
    if (sc) { var r = sc.getBoundingClientRect(); return [r.top, r.bottom]; }
    return [0, w.innerHeight || document.documentElement.clientHeight];
  }
  MW.anchor = {
    sel: null,
    /** 由事件目标找锚点；返回 data-key（无则 null） */
    mark: function (target) {
      if (!target || !target.closest) return null;
      var k = target.closest("[data-key]");
      if (k) { this.sel = '[data-key="' + k.getAttribute("data-key") + '"]'; return k.getAttribute("data-key"); }
      var box = target.closest("[id^='mw-']");
      if (box) this.sel = "#" + box.id;
      return null;
    },
    find: function (sel) {
      var el = MW.$(sel);
      return el && el.offsetParent !== null ? el : null;
    },
    capture: function () {
      if (!this.sel) return null;
      var el = this.find(this.sel);
      if (!el) return null;
      var r = el.getBoundingClientRect(), v = viewRect();
      if (r.bottom < v[0] || r.top > v[1]) return null; // 已滚出屏幕时不以它为准
      return { sel: this.sel, top: layoutTop(el), y: util.scrollY() };
    },
    restore: function (snap) {
      if (!snap) return false;
      var el = this.find(snap.sel);
      if (!el) return false;
      var to = snap.y + layoutTop(el) - snap.top;
      if (Math.abs(util.scrollY() - to) > 2) util.scrollTo(to);
      return true;
    },
    /** 把节点滚到屏幕中部并闪烁提示 */
    reveal: function (el) {
      var v = viewRect();
      var top = util.pageTop(el) - (v[1] - v[0]) / 2 + Math.min(el.offsetHeight, (v[1] - v[0]) / 2) / 2;
      util.scrollTo(top, true);
      el.classList.remove("mw-locate");
      void el.offsetWidth;
      el.classList.add("mw-locate");
      setTimeout(function () { el.classList.remove("mw-locate"); }, 1900);
    },
  };
  /** 在本引擎挂载的组件范围内查找节点 */
  MW.$ = function (sel) {
    var list = MW.partList || [];
    for (var i = 0; i < list.length; i++) {
      var el = list[i].el && list[i].el.querySelector(sel);
      if (el) return el;
    }
    return null;
  };
  /** 挂到 body 上的浮层节点，销毁引擎时统一移除 */
  MW.floating = [];
  MW.addFloating = function (el) {
    el.classList.add("mw-scope");
    document.body.appendChild(el);
    MW.floating.push(el);
    return el;
  };

  /* ================= api ================= */
  var api = {
    URL: "/relay/api?method=Nbroker-UrlProxy",
    FRE_URL: "/relay/rapi/ifob-fre",
    SERVICE_URL: "http://ibroker-idcmaintain:8080/nBroker/api/v1/task",
    UPLOAD_URL: "/relay/cosfile/index",
    TIMEOUT: 12000,
    UPLOAD_TIMEOUT: 60000,
    /** 网络层：断网 / 超时 / 502~504 视为网络错误并切换为离线；任一请求成功即恢复在线 */
    _net: function (p, ms) {
      return util.timeout(p, ms).then(function (v) {
        MW.net.set(false);
        return v;
      }, function (e) {
        if (util.isNetErr(e)) {
          if (!e.offline) e = util.netErr(util.errMsg(e) || "网络不可用");
          MW.net.set(true);
        }
        throw e;
      });
    },
    _transport: function (url, body) {
      if (url === api.URL && host.apiUrl) url = host.apiUrl;
      var p = fetch(url, {
        method: "POST",
        headers: { authToken: sessionStorage.getItem("AuthToken") || "" },
        body: JSON.stringify(body),
      }).then(function (r) {
        if (r.status === 0 || (r.status >= 502 && r.status <= 504)) throw util.netErr("网络异常（" + r.status + "）");
        return r.json();
      }, function (e) {
        throw util.netErr(util.errMsg(e) || "网络不可用");
      });
      return api._net(p, api.TIMEOUT);
    },
    _ctx: function () {
      return store.get("$context") || {};
    },
    _body: function (ctx, args) {
      return {
        SystemId: "2",
        Action: "Nbroker",
        Method: "UrlProxy",
        ServiceUrl: this.SERVICE_URL,
        NbrokerData: { context: ctx, args: args || {} },
      };
    },
    /** 等价原脚本：POST /relay/api?method=Nbroker-UrlProxy，context = {...$context, method_name} */
    call: function (method, args) {
      var ctx = Object.assign({}, this._ctx(), { method_name: method });
      return this._transport(this.URL, this._body(ctx, args));
    },
    /** 自定义 context（如 load_item_cache 只带 service_name / method_name） */
    raw: function (ctx, args) {
      return this._transport(this.URL, this._body(ctx, args));
    },
    /** 人脸识别 / 资质识别走 /relay/rapi/ifob-fre */
    fre: function (method, args) {
      var ctx = Object.assign({}, this._ctx(), { method_name: method });
      return this._transport(this.FRE_URL, this._body(ctx, args));
    },
    /** Ticket.GetHistoryVariables 回填表单 */
    history: function (keys) {
      var ctx = this._ctx();
      return this._transport(this.URL, {
        Keylist: keys,
        SystemId: "2",
        Action: "Ticket",
        InstanceId: ctx.instanceId,
        Method: "GetHistoryVariables",
        VueOperateUser: ctx.user,
      });
    },
    /** 文件上传：返回与平台 Upload 组件一致的 response（{FileList:[{name,url}]}） */
    upload: function (file, name) {
      var fd = new FormData();
      if (name) fd.append("file", file, name);
      else fd.append("file", file);
      var headers = (host.uploadHeaders && host.uploadHeaders()) || {};
      var p = fetch(this.UPLOAD_URL, { method: "POST", headers: headers, body: fd }).then(function (r) {
        if (r.status === 0 || (r.status >= 502 && r.status <= 504)) throw util.netErr("网络异常（" + r.status + "）");
        if (!r.ok) throw new Error("上传失败：" + r.status);
        return r.json();
      }, function (e) {
        throw util.netErr(util.errMsg(e) || "网络不可用");
      });
      return api._net(p, api.UPLOAD_TIMEOUT);
    },
  };
  MW.api = api;

  /* ================= registry：组件校验 ================= */
  MW.registry = {
    list: [],
    register: function (c) {
      this.list.push(c);
    },
    validateAll: function () {
      return this.list.reduce(function (acc, c) {
        return acc.concat(c.el && c.validate ? c.validate() : []);
      }, []);
    },
  };

  /** 汇总提示：同一次提交中多个组件校验失败时只提示一次，并滚动到首个错误 */
  var reportTimer = null;
  MW.reportErrors = function () {
    clearTimeout(reportTimer);
    reportTimer = setTimeout(function () {
      var by = {}, total = 0, off = 0, permMsg = "";
      MW.registry.list.forEach(function (p) {
        var n = 0;
        (p.lastErrors || []).forEach(function (e) {
          if (e.perm) permMsg = e.msg;
          else if (e.offline) off = e.offline;
          else n++;
        });
        if (n) {
          by[p.title] = n;
          total += n;
        }
      });
      if (permMsg) return MW.ui.toast(permMsg, "warn");
      if (off) {
        MW.ui.toast(
          MW.net.offline ? "还有 " + off + " 项离线操作未上传，请到有信号的位置，上传完成后再提交" : "正在上传离线期间的 " + off + " 项操作，请稍候再提交",
          "warn"
        );
        if (MW.sync && !MW.net.offline) MW.sync.flush().catch(function () {});
      }
      if (!total) return;
      MW.ui.toast("还有 " + total + " 项未完成：" + Object.keys(by).map(function (k) { return k + " " + by[k]; }).join("，"), "warn");
      MW.ui.scrollToError();
    }, 120);
  };

  /** 手动校验入口（返回 true 表示通过） */
  MW.validate = function () {
    var errs = MW.registry.validateAll();
    if (errs.length) MW.reportErrors();
    return !errs.length;
  };

  /* ================= start ================= */
  /** 原 confirm 字段脚本中 GetHistoryVariables 的 Keylist */
  MW.HISTORY_KEYS = [
    "main_ticket_id", "campus_name", "mozu_name", "devicetypes_name", "device_number", "maintenance_type",
    "maintenance_cycle", "plan_start_date", "plan_end_date", "sub_ticket_check_in", "check_in_dc", "face_user",
    "face_photo", "upload_face_photo_time", "face_detect_result", "qualification_detect_result", "face_detail",
    "scan_qr_code", "scan_code_result", "upload_photo_time", "upload_start", "txt_start", "sw_start", "upload_end",
    "other_matter", "item_model_select", "item_table", "item_table_temp", "maintain_summary", "txt_end", "sw_end",
    "abnormal_ticket_id", "abnormal_confirm", "abnormal_count", "confirm", "main_step", "qualification_msg_end",
    "face_user_end", "face_photo_end", "upload_face_photo_time_end", "face_detect_result_end",
    "qualification_detect_result_end", "face_detail_end", "confirm_abnormal",
  ];

  /**
   * 启动一次：等平台 setValues 完成（父组件 mounted 晚于子组件）后读取初始值，
   * 再按原逻辑调用 GetHistoryVariables 合并流程变量
   */
  MW.ready = null;
  MW.start = function () {
    if (MW.ready) return MW.ready;
    MW.ready = util
      .delay(0)
      .then(function () {
        store.init(host.read ? host.read() : {});
        if (!api._ctx().instanceId) return null;
        return api.history(MW.sync ? MW.sync.keys() : MW.HISTORY_KEYS);
      })
      .then(function (res) {
        var data = (res && res.data) || {};
        var patch = {};
        Object.keys(data).forEach(function (k) {
          if (k === "_sync_meta") return;
          if (data[k] !== null && data[k] !== undefined) patch[k] = data[k];
        });
        if (Object.keys(patch).length) store.patch(patch, { silent: true });
        if (res && res.data && MW.sync) MW._syncSeed = data;
      })
      .catch(function (e) {
        console.warn("[mw] GetHistoryVariables 失败", e);
        // 打开时恰好断网：以平台带出的表单值作为合并基准启动同步，离线暂存仍可用
        if (MW.sync && util.isNetErr(e)) {
          var seed = {};
          Object.keys(store.values).forEach(function (k) { if (k !== "$context") seed[k] = store.values[k]; });
          MW._syncSeed = seed;
        }
      })
      .then(function () {
        if (host.subscribe) {
          MW._unsub = host.subscribe(function (next) {
            // 平台改写表单值（草稿恢复 / 待办数据刷新 / 联网后重建表单等）时：
            // 由同步机制管理的字段以组件数据为准（服务器最新 + 本机未上传），不接受平台的旧值，
            // 并把组件数据写回表单，保证平台提交时带上的也是最新数据；其余字段照常拉取
            var own = MW.sync && MW.sync.on ? MW.sync.keys() : [];
            var ext = {}, back = [];
            Object.keys(next || {}).forEach(function (k) {
              if (own.indexOf(k) < 0) ext[k] = next[k];
              else if (store.values[k] !== undefined && JSON.stringify(next[k]) !== JSON.stringify(store.values[k])) back.push(k);
            });
            if (back.length) store.repush(back);
            store.pull(ext);
          });
        }
        // 组件挂载后再启动多人协同同步（需要 biz.isClosed 等判断）
        if (MW.biz && MW.biz.permInit) MW.biz.permInit();
        if (MW._syncSeed && MW.sync) {
          var seed = MW._syncSeed;
          MW._syncSeed = null;
          setTimeout(function () { MW.sync.start(seed); }, 0);
        }
      });
    return MW.ready;
  };

  /** 所有组件卸载后销毁：移除浮层与订阅 */
  MW.destroy = function () {
    MW._unsub && MW._unsub();
    MW._unsub = null;
    (MW.cleanups || []).forEach(function (fn) {
      try { fn(); } catch (e) {}
    });
    MW.cleanups = [];
    MW.floating.forEach(function (el) {
      el.remove();
    });
    MW.floating = [];
    document.body.classList.remove("mw-noscroll");
  };
  MW.cleanups = [];
}
